import { useEffect, useState } from 'react';
import AppLayout from '@cloudscape-design/components/app-layout';
import SideNavigation from '@cloudscape-design/components/side-navigation';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import Badge from '@cloudscape-design/components/badge';
import BreadcrumbGroup, { BreadcrumbGroupProps } from '@cloudscape-design/components/breadcrumb-group';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { getMe } from '../api/tickets';
import { isValidAlias, phonetoolUrl } from '../utils/identity';
import { UserAvatar } from '../components/UserAvatar';
import { HELP_LINKS } from '../config/helpLinks';

const SECTION_LABELS: Record<string, string> = {
  tickets: 'Tickets',
  reports: 'Reports',
  experiments: 'AI experiments',  // detail pages only; the list lives in Settings
  settings: 'Settings',
  help: 'User guide',
};

/** Build breadcrumb items from the current path. Dashboard ("/") is always the root. */
function buildCrumbs(pathname: string, search: string): BreadcrumbGroupProps.Item[] {
  const crumbs: BreadcrumbGroupProps.Item[] = [{ text: 'Dashboard', href: '/' }];
  const segments = pathname.split('/').filter(Boolean);
  const query = new URLSearchParams(search);

  // Theme run details: Dashboard > Reports > Root Cause Themes > {runId}
  if (segments[0] === 'reports' && segments[1] === 'themes' && segments[2]) {
    crumbs.push({ text: 'Reports', href: '/reports' });
    crumbs.push({ text: 'Root Cause Themes', href: '/reports?tab=root-cause-themes' });
    crumbs.push({ text: segments[2].slice(0, 8), href: pathname });
    return crumbs;
  }

  // Tickets scoped to a theme run's root cause keep the trail back to the run:
  // Dashboard > Reports > Root Cause Themes > {runId} > Tickets
  const themeRun = query.get('themeRun');
  if (segments[0] === 'tickets' && !segments[1] && themeRun) {
    crumbs.push({ text: 'Reports', href: '/reports' });
    crumbs.push({ text: 'Root Cause Themes', href: '/reports?tab=root-cause-themes' });
    crumbs.push({ text: themeRun.slice(0, 8), href: `/reports/themes/${themeRun}` });
    crumbs.push({ text: 'Tickets', href: pathname + search });
    return crumbs;
  }

  if (segments[0] && SECTION_LABELS[segments[0]]) {
    // The experiments list moved into Settings, so a run's parent crumb must go
    // to that tab rather than to a route that only redirects.
    const base = segments[0] === 'experiments' ? '/settings?tab=experiments' : `/${segments[0]}`;
    crumbs.push({ text: SECTION_LABELS[segments[0]], href: base });
    // Detail routes, e.g. /tickets/:ticketId
    if (segments[1]) {
      crumbs.push({ text: decodeURIComponent(segments[1]), href: pathname });
    }
  }
  return crumbs;
}

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(true);
  const [alias, setAlias] = useState<string | null>(null);
  const crumbs = buildCrumbs(location.pathname, location.search);

  /** Resolve who is signed in, once. Failure is silent by design: the identity
   * chip is decoration, and an error banner over it would be noise. */
  useEffect(() => {
    let active = true;
    getMe()
      .then((res) => {
        if (active && isValidAlias(res.alias)) setAlias((res.alias as string).trim());
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <TopNavigation
        identity={{ title: 'Ticket Insights', href: '/', logo: { src: '/logo.svg', alt: 'Ticket Insights' }, onFollow: (e) => { e.preventDefault(); navigate('/'); } }}
        utilities={[
          {
            type: 'button',
            iconName: 'settings',
            title: 'Settings',
            ariaLabel: 'Settings',
            onClick: () => navigate('/settings'),
          },
          // Identity chip. Omitted entirely rather than rendered blank when the
          // alias is unknown, so it never shows a nameless avatar.
          ...(alias
            ? [
                {
                  type: 'button' as const,
                  text: alias,
                  href: phonetoolUrl(alias),
                  external: true,
                  externalIconAriaLabel: '(opens in a new tab)',
                  iconSvg: <UserAvatar alias={alias} />,
                  title: `${alias} — open Phone Tool`,
                  ariaLabel: `Signed in as ${alias}. Open Phone Tool`,
                },
              ]
            : []),
        ]}
        i18nStrings={{ overflowMenuTriggerText: 'More', overflowMenuTitleText: 'All' }}
      />
      <AppLayout
        navigationOpen={navOpen}
        onNavigationChange={({ detail }) => setNavOpen(detail.open)}
        // Suppress the tools panel outright. Cloudscape renders the drawer and
        // its trigger by default, so removing the panel's content is not enough
        // — it left an empty side panel behind.
        toolsHide
        breadcrumbs={
          <BreadcrumbGroup
            items={crumbs}
            onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }}
          />
        }
        navigation={
          <SideNavigation
            header={{ text: 'Ticket Insights', href: '/' }}
            activeHref={location.pathname}
            items={[
              { type: 'link', text: 'Dashboard', href: '/' },
              { type: 'link', text: 'Tickets', href: '/tickets' },
              { type: 'link', text: 'Reports', href: '/reports' },
              // Help and feedback, grouped at the bottom and visually separated
              // from the app's own destinations because these leave the app.
              // Rendered only when a destination is configured, so the nav never
              // offers a link that 404s.
              ...(HELP_LINKS.length
                ? [
                    { type: 'divider' as const },
                    {
                      type: 'section' as const,
                      text: 'Help & feedback',
                      items: HELP_LINKS.map((l) => ({
                        type: 'link' as const,
                        text: l.text,
                        href: l.href,
                        external: l.external,
                        externalIconAriaLabel: l.external ? '(opens in a new tab)' : undefined,
                        // Marks a stub, so nobody expects content that is not
                        // written yet.
                        info: l.badge ? <Badge color="grey">{l.badge}</Badge> : undefined,
                      })),
                    },
                  ]
                : []),
            ]}
            onFollow={(e) => {
              // Let external links behave as links; only in-app routes are
              // handled by the router. Without this the SPA would try to route
              // to an absolute URL and go nowhere.
              if (e.detail.external) return;
              e.preventDefault();
              navigate(e.detail.href);
            }}
          />
        }
        content={<Outlet />}
      />
    </>
  );
}
